import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  getUserId,
  getSessionData,
} from "./localStorageService";
import { isTokenExpiringSoon } from "./tokenUtils";
import { SelfUserClassificationEnum } from "../../shared/constants/constants";
import { getApiBaseUrl } from "../../shared/config/tenantConfiguration";

const axiosInstance = axios.create({
  headers: { "Content-Type": "application/json" },
});

// ========================================
// PROACTIVE TOKEN REFRESH
// ========================================

let isRefreshing = false; // Prevent multiple simultaneous refresh calls
let refreshPromise = null; // Store the refresh promise to reuse

/**
 * Refresh access token using refresh token
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
const refreshAccessToken = async () => {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing && refreshPromise) {
    console.log('🔄 Token refresh already in progress, waiting...');
    return refreshPromise;
  }

  isRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();
      const userId = getUserId();

      if (!refreshToken) {
        console.error('❌ No refresh token available');
        return null;
      }

      console.log('🔄 Refreshing access token proactively...');

      const response = await axios.post(
        `${getApiBaseUrl()}/api/bpm/cmd`,
        {
          cmd: "LoginRefreshTokenCommand",
          data: JSON.stringify({
            userId,
            refreshToken,
          }),
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const newAccessToken = response?.data?.outData?.accessToken;

      if (newAccessToken) {
        console.log('✅ Token refreshed successfully');
        setAccessToken(newAccessToken);
        return newAccessToken;
      } else {
        console.error('❌ Token refresh failed: No access token in response');
        return null;
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ========================================
// REQUEST INTERCEPTOR (PROACTIVE CHECK)
// ========================================

axiosInstance.interceptors.request.use(
  async (config) => {
    // Dynamically set baseURL for each request to support tenant switching
    config.baseURL = getApiBaseUrl();
    
    const accessToken = getAccessToken();
    
    // Skip token refresh for the refresh token request itself
    if (config.url?.includes('LoginRefreshTokenCommand')) {
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    }

    if (accessToken) {
      // ✅ PROACTIVE CHECK: Is token expiring soon? (< 1 minute)
      if (isTokenExpiringSoon(accessToken, 1)) {
        console.log('⏰ Token expiring soon, refreshing proactively...');
        
        const newToken = await refreshAccessToken();
        
        if (newToken) {
          // Use new token for this request
          config.headers.Authorization = `Bearer ${newToken}`;
          console.log('✅ Request will use refreshed token');
        } else {
          // Refresh failed - try with existing token (fallback)
          console.warn('⚠️ Token refresh failed, using existing token');
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } else {
        // Token still valid, use it
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ========================================
// RESPONSE INTERCEPTOR (FALLBACK FOR 401)
// ========================================

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const refreshToken = getRefreshToken();
    const originalRequest = error?.config;
    const sessionData = getSessionData();
    const classification = sessionData?.userClassification;

    console.log("error", error?.response?.status);
    console.log("refreshToken", !!refreshToken);
    console.log("originalRequest._retry", originalRequest?._retry);

    // ========================================
    // SCENARIO 1: No refresh token at all
    // ========================================
    if (error?.response?.status === 401 && !refreshToken) {
      console.log("❌ No refresh token available, redirecting to login");
      
      localStorage.clear();
      localStorage.setItem("isLoggedIn", false);
      localStorage.setItem("logoutEvent", Date.now());
      
      if (
        classification ===
        SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
      ) {
        window.location.href = "/authentication/admin/login";
      } else {
        window.location.href = "/authentication/login";
      }
      
      return Promise.reject(error);
    }

    // ========================================
    // SCENARIO 2: 401 but haven't tried refresh yet
    // (Fallback if proactive refresh didn't work)
    // ========================================
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      console.log("⚠️ 401 error despite proactive refresh, trying one more time...");
      
      try {
        const newToken = await refreshAccessToken();
        
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } else {
          // Refresh failed - redirect to login
          console.error("❌ Token refresh failed, redirecting to login");
          
          localStorage.clear();
          localStorage.setItem("isLoggedIn", false);
          localStorage.setItem("logoutEvent", Date.now());
          
          if (
            classification ===
            SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
          ) {
            window.location.href = "/authentication/admin/login";
          } else {
            window.location.href = "/authentication/login";
          }
          
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error("❌ Refresh error, redirecting to login");
        
        localStorage.clear();
        localStorage.setItem("isLoggedIn", false);
        localStorage.setItem("logoutEvent", Date.now());
        
        if (
          classification ===
          SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
        ) {
          window.location.href = "/authentication/admin/login";
        } else if (classification) {
          window.location.href = "/authentication/login";
        }
        
        return Promise.reject(refreshError);
      }
    }

    // ========================================
    // SCENARIO 3: Already retried, give up
    // ========================================
    if (error?.response?.status === 401) {
      console.error("❌ Still 401 after retry, redirecting to login");
      
      localStorage.clear();
      localStorage.setItem("isLoggedIn", false);
      localStorage.setItem("logoutEvent", Date.now());
      
      if (
        classification ===
        SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
      ) {
        window.location.href = "/authentication/admin/login";
      } else {
        window.location.href = "/authentication/login";
      }
      
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// export the axios instance
export default axiosInstance;
